import { useRef } from 'react'

import { IActionRepeaterValue, IActionType, TypographySelectValue } from '@makyoapp/makyo-common'
import { ColorVariant, CornerRadius, MaterialIcon, RawSpacingInput } from '@makyoapp/makyo-tw-ui'
import { HoverEffectVariant } from '@makyoapp/makyo-tw-ui/dist/lib/utils/hover-effects'

import { CoreComponentAction, CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompButtonProps } from './comp'

export interface CompButtonWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  form: CoreComponentFormProps
  children: (props: CompButtonProps, ref: React.RefObject<HTMLButtonElement>) => React.ReactNode
}

export function CompButtonWrapperCore({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, rowId: detailRowId, currentParentBg },
  action: { simulateAction, actionLoadingMap },
  form: { dataSource, handleFormSubmit, isFormLoading, requiredFieldsStatus, formErrors },
  children,
}: CompButtonWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  const btnRef = useRef<HTMLButtonElement>(null)

  const isLoading = actionLoadingMap.get(component._id) || false

  // ========================= data =========================
  const label = useParseVariableText(
    settings[ComponentSettingKey.ButtonDataLabel],
    containerClient,
    detailTableId,
    detailRowIndex,
    detailRowId
  )

  const icon = useParseVariableSelect<string>(
    settings[ComponentSettingKey.ButtonDataIcon],
    containerClient,
    detailTableId,
    detailRowIndex,
    detailRowId
  )

  const badge = useParseVariableText(
    settings[ComponentSettingKey.ButtonDataBadge],
    containerClient,
    detailTableId,
    detailRowIndex,
    detailRowId
  )

  // ========================= design =========================

  // base
  const fullWidth = settings[ComponentSettingKey.ButtonDesignFullWidth] as boolean
  const alignment = settings[ComponentSettingKey.ButtonDesignHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.ButtonDesignMargin] as RawSpacingInput
  const padding = settings[ComponentSettingKey.ButtonDesignPadding] as RawSpacingInput

  const size = (settings[ComponentSettingKey.ButtonDesignSize] as 'sm' | 'md' | 'lg' | 'icon') ?? 'md'
  const cornerRadius = settings[ComponentSettingKey.ButtonDesignShape] as CornerRadius
  const isTrailing = settings[ComponentSettingKey.ButtonDesignIconPosition] === 'trailing'
  const variant = settings[ComponentSettingKey.ButtonDesignVariant] as 'filled' | 'outlined'
  const colorVariant = settings[ComponentSettingKey.ButtonDesignColorVariant] || 'primary-40'
  const labelTypography = (settings[ComponentSettingKey.ButtonDesignTypography] as TypographySelectValue) ?? {
    typography: 'Label Medium',
  }

  const hoverEffect = settings[ComponentSettingKey.ButtonDesignHoverEffect] as HoverEffectVariant

  // color
  // label
  const textColor = settings[ComponentSettingKey.ButtonDesignTextColor] || 'adaptive'
  const textColorSolid = (settings[ComponentSettingKey.ButtonDesignTextColorSolid] as ColorVariant) || 'primary-40'
  const textColorStartGradient =
    (settings[ComponentSettingKey.ButtonDesignTextColorStartGradient] as ColorVariant) || 'primary-40'
  const textColorEndGradient =
    (settings[ComponentSettingKey.ButtonDesignTextColorEndGradient] as ColorVariant) || 'primary-40'
  const textColorGradientDirection = settings[
    ComponentSettingKey.ButtonDesignTextColorGradientDirection
  ] as ColorVariant

  // badge
  const badgeColor = settings[ComponentSettingKey.ButtonDesignBadgeColor] || 'adaptive'
  const badgeColorSolid = (settings[ComponentSettingKey.ButtonDesignBadgeColorSolid] as ColorVariant) || 'primary-40'
  const badgeColorStartGradient =
    (settings[ComponentSettingKey.ButtonDesignBadgeColorStartGradient] as ColorVariant) || 'primary-40'
  const badgeColorEndGradient =
    (settings[ComponentSettingKey.ButtonDesignBadgeColorEndGradient] as ColorVariant) || 'primary-40'
  const badgeColorGradientDirection = settings[
    ComponentSettingKey.ButtonDesignBadgeColorGradientDirection
  ] as ColorVariant

  // badge background
  const badgeBackgroundColor = settings[ComponentSettingKey.ButtonDesignBadgeBackgroundColorType] || 'adaptive'
  const badgeBackgroundColorSolid =
    (settings[ComponentSettingKey.ButtonDesignBadgeBackgroundColorSolid] as ColorVariant) || 'primary-40'
  const badgeBackgroundColorStartGradient =
    (settings[ComponentSettingKey.ButtonDesignBadgeBackgroundColorStartGradient] as ColorVariant) || 'primary-40'
  const badgeBackgroundColorEndGradient =
    (settings[ComponentSettingKey.ButtonDesignBadgeBackgroundColorEndGradient] as ColorVariant) || 'primary-40'
  const badgeBackgroundColorGradientDirection = settings[
    ComponentSettingKey.ButtonDesignBadgeBackgroundColorGradientDirection
  ] as ColorVariant

  const badgeTopPosition = settings[ComponentSettingKey.ButtonDesignBadgeTopPosition] as number
  const badgeRightPosition = settings[ComponentSettingKey.ButtonDesignBadgeRightPosition] as number

  // background
  const backgroundColor = settings[ComponentSettingKey.ButtonDesignColorType] || 'adaptive'
  const backgroundColorSolid =
    (settings[ComponentSettingKey.ButtonDesignColorTypeSolid] as ColorVariant) || 'primary-40'
  const backgroundColorStartGradient =
    (settings[ComponentSettingKey.ButtonDesignColorTypeStartGradient] as ColorVariant) || 'primary-40'
  const backgroundColorEndGradient =
    (settings[ComponentSettingKey.ButtonDesignColorTypeEndGradient] as ColorVariant) || 'primary-40'
  const backgroundColorGradientDirection = settings[
    ComponentSettingKey.ButtonDesignColorTypeGradientDirection
  ] as ColorVariant

  const rawBadgePadding = settings[ComponentSettingKey.ButtonDesignBadgePadding] as RawSpacingInput

  const getValue = (val: any, def: number) => val?.value ?? val ?? def

  const badgePadding = rawBadgePadding
    ? {
        top: getValue(rawBadgePadding.top, 2),
        right: getValue(rawBadgePadding.right, 6),
        bottom: getValue(rawBadgePadding.bottom, 2),
        left: getValue(rawBadgePadding.left, 6),
      }
    : undefined

  // ========================= actions =========================
  const actions = getSafely(settings, [ComponentSettingKey.ButtonActionClick], []) as IActionRepeaterValue[]
  const hasSubmitAction = actions.some((a) => [IActionType.FormAddRow, IActionType.FormUpdateRow].includes(a.type))
  const isSubmitButton = !!dataSource && (Boolean(component._IsSubmit) || hasSubmitAction)

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    if (isSubmitButton && hasSubmitAction) {
      await handleFormSubmit(actions)
      return
    }
    if (!!actions.length) {
      await simulateAction({
        actions,
        elRef: btnRef,
        external: {
          detailsPage: {
            detailTableId,
            detailRowIndex,
            detailRowId,
          },
        },
        triggerSource: {
          id: component._id,
          label: component._Title || component._ComponentID,
          type: 'component',
        },
      })
      return
    }
  }

  // ========================= derived =========================
  const hasEmptyRequiredFields = isSubmitButton ? Object.values(requiredFieldsStatus).some((isEmpty) => isEmpty) : false
  const hasError = isSubmitButton ? Object.values(formErrors).some((error) => !!error) : false

  const isDisabled = (isSubmitButton && isLoading) || isFormLoading || hasError || hasEmptyRequiredFields

  const btnProps: CompButtonProps = {
    // base
    id: component._id,
    alignment,
    margin,
    padding,
    backgroundColor: currentParentBg,

    // button
    fullWidth,
    variant,
    colorVariant,
    size,
    cornerRadius,
    labelTypography,
    trailingIcon: isTrailing,
    icon: icon ? <MaterialIcon icon={icon} size={24} /> : undefined,
    badgeText: badge,
    disabled: isDisabled,
    onClick: handleClick,
    className: component._IsSubmit ? 'mk-form-submit-button' : '',
    children: label,

    // color
    textColorType: textColor,
    textColorSolid: textColorSolid,
    textColorStartGradient: textColorStartGradient,
    textColorEndGradient: textColorEndGradient,
    textColorGradientDirection: textColorGradientDirection,
    badgeColorType: badgeColor,
    badgeColorSolid: badgeColorSolid,
    badgeColorStartGradient: badgeColorStartGradient,
    badgeColorEndGradient: badgeColorEndGradient,
    badgeColorGradientDirection: badgeColorGradientDirection,
    backgroundColorType: backgroundColor,
    backgroundColorSolid: backgroundColorSolid,
    backgroundColorStartGradient: backgroundColorStartGradient,
    backgroundColorEndGradient: backgroundColorEndGradient,
    backgroundColorGradientDirection: backgroundColorGradientDirection,
    badgeBackgroundColorType: badgeBackgroundColor,
    badgeBackgroundColorSolid: badgeBackgroundColorSolid,
    badgeBackgroundColorStartGradient: badgeBackgroundColorStartGradient,
    badgeBackgroundColorEndGradient: badgeBackgroundColorEndGradient,
    badgeBackgroundColorGradientDirection: badgeBackgroundColorGradientDirection,
    badgeTop: badgeTopPosition ? `${badgeTopPosition}px` : undefined,
    badgeRight: badgeRightPosition ? `${badgeRightPosition}px` : undefined,
    badgePadding: badgePadding as any,
    devMode: isDevelopmentMode,
    // hover effect
    hoverEffect,
    hoverIntensity: 'md',
  }
  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block' onClick={() => openComponentSettings(component)}>
        {children(btnProps, btnRef)}
      </div>
    )
  }
  return children(btnProps, btnRef)
}
